/**
 * How the reader last arranged the Watch page: theater mode, and whether the chapters and
 * recommendations panels are shown. Persisted so hiding the rail is a decision about the app rather
 * than about one video — the next Watch page opens the way this one was left.
 *
 * Its own key rather than a field on `visious.player`: that store is *playback* state read on the
 * player's own paths, and the settings-store lesson (one setting, one key — `platform/settings.ts`)
 * says not to give a value a second authoritative-looking home.
 */

const KEY = 'visious.watchLayout';

export interface WatchLayoutPrefs {
  readonly theater: boolean;
  readonly chapters: boolean;
  readonly recommendations: boolean;
  /** The fold-away comments — the heading stays, the body goes, and the choice persists. */
  readonly comments: boolean;
  /** The playlist panel, shown whenever the route carries a `list`. Hideable like the rest. */
  readonly playlist: boolean;
  /**
   * Play the next entry when this one ends — the playlist panel's own switch, and the one
   * value in here that is a *behaviour* rather than an arrangement. It lives with the arrangement
   * anyway: the same page reads it, on the same path, at the same moment, and a fourth store for
   * one boolean is exactly the second authoritative home `platform/settings.ts` warns about. It is
   * **not** the Playback tab's `autoplay`, which is about starting this video on arrival.
   */
  readonly playlistAutoplay: boolean;
  /**
   * The same switch for the **queue**, and a separate value because the two lists ask a different
   * question. A playlist is a collection somebody may be dipping into, and its advance *navigates*, so
   * being carried to the next entry is a thing to ask for. A queue is a list of what to play next and
   * its advance happens where the reader is, so it plays on unless they say otherwise — which is why
   * this one defaults **on** and `playlistAutoplay` defaults off.
   */
  readonly queueAutoplay: boolean;
}

export const DEFAULT_WATCH_LAYOUT: WatchLayoutPrefs = {
  theater: false,
  chapters: true,
  recommendations: true,
  comments: true,
  playlist: true,
  // Off by default, for the reason the Playback tab's autoplay is: navigating on your behalf is a
  // thing to be asked for. §8's principle, and the same default.
  playlistAutoplay: false,
  // On by default, and the opposite default for the opposite reason: a queue is a list of what to play
  // next, and one that stopped between every two videos would not be a queue.
  queueAutoplay: true,
};

export function readWatchLayout(): WatchLayoutPrefs {
  let raw: unknown;
  try {
    const text = globalThis.localStorage.getItem(KEY);
    if (text === null) return DEFAULT_WATCH_LAYOUT;
    raw = JSON.parse(text) as unknown;
  } catch {
    // Storage can throw outright in a partitioned context, and a half-written value is possible on
    // any origin. Neither is a reason to open the page in an unknown shape.
    return DEFAULT_WATCH_LAYOUT;
  }

  if (typeof raw !== 'object' || raw === null) return DEFAULT_WATCH_LAYOUT;
  const record = raw as Record<string, unknown>;

  return {
    theater: record['theater'] === true,
    // The panels default to *shown*, so anything unreadable falls back to the whole page.
    chapters: record['chapters'] !== false,
    recommendations: record['recommendations'] !== false,
    comments: record['comments'] !== false,
    playlist: record['playlist'] !== false,
    // The one that defaults *off*, so an unreadable value must not turn it on.
    playlistAutoplay: record['playlistAutoplay'] === true,
    // And its mirror: this one defaults *on*, so an unreadable value must not turn it off.
    queueAutoplay: record['queueAutoplay'] !== false,
  };
}

export function writeWatchLayout(prefs: WatchLayoutPrefs): void {
  try {
    globalThis.localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    // A layout that cannot be saved is not worth failing the page over.
  }
}

/** The Data tab's "clear local settings" — back to the default arrangement. Read on mount only, so
 * an open Watch page keeps its current layout until the next one. */
export function clearWatchLayout(): void {
  try {
    globalThis.localStorage.removeItem(KEY);
  } catch {
    // Nothing to recover: the default applies wherever the read fails too.
  }
}
